import React, { useEffect, useMemo, useState } from 'react';
import { consigneesAPI, warehouseBinsAPI, warehouseRowsAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const WarehouseBinsManagement = () => {
  const [bins, setBins] = useState([]);
  const [rows, setRows] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBin, setEditingBin] = useState(null);
  const [filterConsigneeId, setFilterConsigneeId] = useState('');
  const [formData, setFormData] = useState({
    warehouse_row_id: '',
    bin_name: '',
    bin_number: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // refresh bins when filter changes
    loadBins();
  }, [filterConsigneeId]);

  const loadBins = async () => {
    try {
      setLoading(true);
      const binsData = await warehouseBinsAPI.getAll(
        filterConsigneeId ? { consignee_id: filterConsigneeId } : {}
      );
      setBins(binsData);
    } catch (error) {
      alert('Error loading bins: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [binsData, rowsData, consigneesData] = await Promise.all([
        warehouseBinsAPI.getAll(),
        warehouseRowsAPI.getAll(),
        consigneesAPI.getAll(),
      ]);
      setBins(binsData);
      setRows(rowsData);
      setConsignees(consigneesData);
    } catch (error) {
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      warehouse_row_id: '',
      bin_name: '',
      bin_number: '',
      is_active: true,
    });
  };

  const rowsForSelectedConsignee = useMemo(() => {
    if (!filterConsigneeId) return rows;
    const cid = parseInt(filterConsigneeId);
    return rows.filter((r) => r.consignee_id === cid);
  }, [rows, filterConsigneeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        warehouse_row_id: parseInt(formData.warehouse_row_id),
        bin_name: formData.bin_name,
        bin_number: formData.bin_number === '' ? null : parseInt(formData.bin_number),
        is_active: !!formData.is_active,
      };
      if (editingBin) {
        await warehouseBinsAPI.update(editingBin.id, submitData);
      } else {
        await warehouseBinsAPI.create(submitData);
      }
      setShowModal(false);
      setEditingBin(null);
      resetForm();
      loadBins();
    } catch (error) {
      alert('Error saving warehouse bin: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingBin(item);
    setFormData({
      warehouse_row_id: item.warehouse_row_id,
      bin_name: item.bin_name || '',
      bin_number: item.bin_number ?? '',
      is_active: !!item.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this warehouse bin?')) return;
    try {
      await warehouseBinsAPI.delete(id);
      loadBins();
    } catch (error) {
      alert('Error deleting warehouse bin: ' + error.message);
    }
  };

  const columns = [
    { key: 'consignee_name', label: 'Consignee' },
    { key: 'row_name', label: 'Row' },
    { key: 'bin_name', label: 'Bin Name' },
    { key: 'bin_number', label: 'Bin #' },
    { key: 'is_active', label: 'Active', format: (val) => (val ? 'Yes' : 'No') },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Warehouse Bins</h1>
        <button className="btn-primary" onClick={() => { setEditingBin(null); resetForm(); setShowModal(true); }}>
          Add Warehouse Bin
        </button>
      </div>

      <div className="form-row" style={{ marginBottom: 12 }}>
        <div className="form-group" style={{ maxWidth: 360 }}>
          <label>Filter by Consignee</label>
          <select value={filterConsigneeId} onChange={(e) => setFilterConsigneeId(e.target.value)}>
            <option value="">All Consignees</option>
            {consignees.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table data={bins} columns={columns} onEdit={handleEdit} onDelete={handleDelete} />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingBin(null); resetForm(); }}
        title={editingBin ? 'Edit Warehouse Bin' : 'Add Warehouse Bin'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Warehouse Row *</label>
            <select
              value={formData.warehouse_row_id}
              onChange={(e) => setFormData({ ...formData, warehouse_row_id: e.target.value })}
              required
            >
              <option value="">Select Row</option>
              {(rowsForSelectedConsignee || rows).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.consignee_name ? `${r.consignee_name} - ` : ''}{r.row_name}{r.row_number != null ? ` (#${r.row_number})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Bin Name *</label>
              <input
                type="text"
                value={formData.bin_name}
                onChange={(e) => setFormData({ ...formData, bin_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Bin Number</label>
              <input
                type="number"
                value={formData.bin_number}
                onChange={(e) => setFormData({ ...formData, bin_number: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={!!formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />{' '}
              Active
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingBin(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WarehouseBinsManagement;

